import {
  ComponentProps,
  forwardRef,
  useImperativeHandle,
} from "react";
import { SubmitHandler, useForm } from "react-hook-form";

import { Button } from "@design-system/Button/Button";
import { FieldGroup } from "@design-system/FieldGroup/FieldGroup";
import { Form } from "@design-system/Form/Form";
import { TextField } from "@design-system/TextField/TextField";

export type ClientFilterFormValues = {
  nameSearch?: string | undefined;
  domainSearch?: string | undefined;
  emailSearch?: string | undefined;
};

export type ClientFilterFormRef = {
  getCurrentValues: () => ClientFilterFormValues;
};

type Props = {
  submitText?: string;
  onFilterChange: SubmitHandler<ClientFilterFormValues>;
  values: ClientFilterFormValues;
  errorsAPI?: ComponentProps<typeof Form>["errorsAPI"];
  disabled?: boolean;
  withSubmitButton?: boolean;
};

export const ClientFilterForm = forwardRef<ClientFilterFormRef, Props>(
  ({ onFilterChange, values, errorsAPI }, ref) => {
    const { handleSubmit, setValue, watch, getValues } =
      useForm<ClientFilterFormValues>({
        values: {
          nameSearch: values.nameSearch ?? undefined,
          domainSearch: values.domainSearch ?? undefined,
          emailSearch: values.emailSearch ?? undefined,
        },
      });

    const nameSearch = watch("nameSearch");
    const domainSearch = watch("domainSearch");
    const emailSearch = watch("emailSearch");

    // Expose getCurrentValues method to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        getCurrentValues: () => getValues(),
      }),
      [getValues],
    );

    return (
      <Form onSubmit={handleSubmit(onFilterChange)} errorsAPI={errorsAPI}>
        <FieldGroup label="Tenant name">
          <TextField
            placeholder="Search by tenant name..."
            value={nameSearch ?? ""}
            onChange={(e) =>
              setValue("nameSearch", e.target.value || undefined, {
                shouldDirty: true,
              })
            }
          />
        </FieldGroup>

        <FieldGroup label="Domain">
          <TextField
            placeholder="Search by domain..."
            value={domainSearch ?? ""}
            onChange={(e) =>
              setValue("domainSearch", e.target.value || undefined, {
                shouldDirty: true,
              })
            }
          />
        </FieldGroup>

        <FieldGroup label="Email address">
          <TextField
            placeholder="Search by email address..."
            value={emailSearch ?? ""}
            onChange={(e) =>
              setValue("emailSearch", e.target.value || undefined, {
                shouldDirty: true,
              })
            }
          />
        </FieldGroup>

        {/* Hidden submit button to enable Enter key submission */}
        <Button type="submit" style={{ display: "none" }} />
      </Form>
    );
  },
);